/**
 * action.json in, JSON payload out.
 *
 * The whole contract: read `input_schema`, render every field, hold state,
 * validate, emit. Zero per-connector code — a connector added to the backend
 * this afternoon gets a working form with no frontend change at all.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { JsonSchema } from '@/core/types/api.types'
import {
  defaultsFor,
  isRequired,
  labelFor,
  orderedKeys,
  pruneEmpty,
} from '@/core/utils/schema.utils'
import { validate } from './validation.engine'
import { resolveField } from './field.resolver'

interface Props {
  /** Stable identity for the validator cache, e.g. `github:create_issue`. */
  cacheKey: string
  schema: JsonSchema
  connectionId: string | null
  onSubmit: (input: Record<string, unknown>) => void
  submitLabel?: string
  busy?: boolean
  disabled?: boolean
  /** Field errors reported by the server, merged over local ones. */
  serverErrors?: Record<string, string>
  footerExtra?: ReactNode
  onReset?: () => void
}

export function SchemaForm({
  cacheKey,
  schema,
  connectionId,
  onSubmit,
  submitLabel = 'Run action',
  busy,
  disabled,
  serverErrors,
  footerExtra,
  onReset,
}: Props) {
  const initial = useMemo(() => defaultsFor(schema), [schema])
  const [values, setValues] = useState<Record<string, unknown>>(initial)
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})
  // Errors only appear after a submit attempt. Scolding someone for an empty
  // required field they have not reached yet is hostile.
  const [submitted, setSubmitted] = useState(false)

  // Switching action resets the form to the new schema's defaults.
  useEffect(() => {
    setValues(initial)
    setLocalErrors({})
    setSubmitted(false)
  }, [initial, cacheKey])

  const keys = orderedKeys(schema)

  // Built once so a dependent dropdown can say "Choose Author first" using the
  // same label the user reads above the parent field.
  const siblingLabels = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [key, prop] of Object.entries(schema.properties ?? {})) {
      out[key] = labelFor(key, prop)
    }
    return out
  }, [schema])

  const setValue = useCallback((key: string, next: unknown) => {
    setValues((prev) => {
      const copy = { ...prev }
      if (next === undefined) delete copy[key]
      else copy[key] = next
      return copy
    })
  }, [])

  const errors = useMemo(
    () => ({ ...localErrors, ...(serverErrors ?? {}) }),
    [localErrors, serverErrors],
  )

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const payload = pruneEmpty(values)
    const result = validate(cacheKey, schema, payload)

    setSubmitted(true)
    setLocalErrors(result.errors)
    if (!result.valid) return

    onSubmit(payload)
  }

  const renderOne = (key: string): ReactNode => {
    const childSchema = schema.properties![key]

    // Composites recurse through this same closure, so nesting depth is not
    // something any individual field component has to think about.
    const renderChild: Parameters<typeof resolveField>[0]['renderChild'] = (childArgs) =>
      resolveField({
        ...childArgs,
        connectionId,
        formValues: values,
        siblingLabels,
        renderChild: (deeper) =>
          resolveField({
            ...deeper,
            connectionId,
            formValues: values,
            siblingLabels,
            renderChild: () => null,
          }),
      })

    return (
      <div key={key}>
        {resolveField({
          name: key,
          path: key,
          schema: childSchema,
          value: values[key],
          onChange: (next) => setValue(key, next),
          error: submitted || serverErrors?.[key] ? errors[key] : undefined,
          required: isRequired(schema, key),
          disabled: disabled || busy,
          connectionId,
          formValues: values,
          siblingLabels,
          renderChild,
        })}
      </div>
    )
  }

  const hasFields = keys.length > 0

  return (
    <form onSubmit={handleSubmit} className="stack" noValidate>
      {hasFields ? (
        keys.map(renderOne)
      ) : (
        <p className="muted small" style={{ margin: 0 }}>
          This action takes no input.
        </p>
      )}

      <div className="row" style={{ marginTop: 4 }}>
        <button className="btn btn-primary" type="submit" disabled={busy || disabled}>
          {busy && <span className="spinner" />}
          {busy ? 'Running…' : submitLabel}
        </button>
        {onReset && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => {
              setValues(initial)
              setLocalErrors({})
              setSubmitted(false)
              onReset()
            }}
          >
            Reset
          </button>
        )}
        <div className="spacer" />
        {footerExtra}
      </div>
    </form>
  )
}
